// ============================================================
// billWordingHtml.ts — B3: the bill PDF's LEGAL WORDING sections (the
// draft-wording banner, the bill label, the signature line, the notices and
// the SRA footer) as HTML. Pure.
// TWO COPIES, the same code below this header: src/utils/billWordingHtml.ts
// (the browser's print / preview — src/utils/billPdf.ts) and
// backend/src/lib/billWordingHtml.ts (the emailed PDF — lib/billPdfHtml.ts).
// backend/test/billWordingParity.test.ts fails if they drift. The TEXT is
// never made here: it comes in the bill payload (letterhead.wording — made by
// backend lib/billWording; frozen in issueSnapshot on an issued bill).
// ============================================================

export interface BillWordingBlock {
  version: 1
  approved: boolean
  approvedBy: string | null
  approvedAt: string | null
  statute: boolean
  label: { heading: string; sentence: string }
  rights: string | null
  complaints: string
  sraFooter: string
  signatureLine: string | null
}

export const DRAFT_WORDING_BANNER = 'DRAFT WORDING: NOT FOR SENDING'
const escW = (s: string | null | undefined): string =>
  (s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')

export const WORDING_CSS = `
  .wbanner { margin-top: 9pt; border: 1.5pt solid #c23b2c; border-radius: 6pt; background: #fdecea; color: #8a1f14; padding: 7pt 10pt; font-size: 9pt; line-height: 1.45; break-inside: avoid; }
  .wbanner b { display: block; font-size: 11pt; letter-spacing: .06em; }
  .blabel { margin-top: 10pt; border: .75pt solid #d8e0dc; border-left: 3pt solid #1a2420; border-radius: 5pt; padding: 7pt 10pt; font-size: 9.6pt; line-height: 1.5; break-inside: avoid; }
  .blabel b { display: block; font-size: 10.5pt; letter-spacing: .08em; }
  .sigtext { font-size: 9.6pt; line-height: 1.5; margin-top: 3pt; }
`

// Until a solicitor approves the firm's wording: on every preview / PDF.
export const wordingBannerHtml = (w: BillWordingBlock | null | undefined): string =>
  w && !w.approved
    ? `<div class="wbanner"><b>${escW(DRAFT_WORDING_BANNER)}</b>The legal wording on this bill has not been approved by a solicitor at the firm. It can’t be issued or sent to a client until it is (Settings → Bill wording).</div>`
    : ''
// "INTERIM STATUTE BILL. This is an interim statute bill. …"
export const billLabelHtml = (w: BillWordingBlock | null | undefined): string =>
  w ? `<div class="blabel"><b>${escW(w.label.heading)}</b>${escW(w.label.sentence)}</div>` : ''
// "Signed: Jane Smith, Partner, authorised to sign on behalf of … Date: …"
export const signatureTextHtml = (w: BillWordingBlock | null | undefined): string =>
  w?.signatureLine ? `<div class="sigtext">${escW(w.signatureLine)}</div>` : ''
// Your rights (statute bills) + complaints.
export const noticesHtml = (w: BillWordingBlock): string =>
  `<div class="sec keep"><div class="sec-h"><i></i>Your rights</div>
      <div class="fine">${w.rights ? `<b>Your rights.</b> ${escW(w.rights)}<br>` : ''}<b>Complaints.</b> ${escW(w.complaints)}</div></div>`
export const sraFooterHtml = (w: BillWordingBlock): string => escW(w.sraFooter)
